import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import {
  AggregateSelectForm,
  CreateNewReportGraphType,
  NewLiveReportGraph,
  NewLiveReportGraphDataSettingsDataBaseCharts,
  NewLiveReportGraphDataSettingsDataNumberChart,
  OrderByChartSettings,
  ReportChartGraphType,
  ReportTimeRange,
  SettingsDateStep,
} from '@app/interfaces/report.type';

import useTranslation from '@app/hooks/use-translation';
import {
  Button,
  CircleButton,
  ColorInput,
  CustomDatePicker,
  EasyModal,
  Input,
  SegmentedControl,
  Select,
  Toggle,
} from '@ui';
import useChartDateIntervals from '@app/hooks/use-chart-date-intervals';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useOutletContext, useParams } from 'react-router-dom';
import {
  useAddNewLiveReportGraphMutation,
  useEditNewLiveReportGraphMutation,
} from '@app/store/api/live-reports-charts.api';
import { chartsTypeData } from '@app/pages/reports/data/report-status-properties.data';
import cn from 'classnames';
import ConditionalFormattingNestedField from '@app/pages/reports/blocks/chart-modal/conditional-formatting-nested-field';
import { IconNameType } from '@app/components/ui/icons/icon-name.type';
import Icon from '@app/components/ui/icons';

import { ChartsLayoutType } from '@app/interfaces/dashboards.type';
import { changeChartLoading } from '@app/store/reducers/report-charts.slice';

import {
  useAddNewChatLiveReportGraphMutation,
  useEditNewChatLiveReportGraphMutation,
} from '@app/store/api/chat-api/chat-live-reports-charts.api';

import { IndividualGptMetricType } from '@app/interfaces/gpt-metric.type';

import { generateColors } from '@app/utils/color-generate';

import { UniversalFilterConstants } from '@app/hooks/use-filter-data';

import moment from 'moment';

import { ReportOutletContext } from '../../report.outlet';

import PieChartPalette from './pie-chart-palette';

type SettingsFormBlockProps = {
  setup?: NewLiveReportGraph;
  isOpen: boolean;
  onClose: (status: boolean) => void;
  onRemove?: (status: boolean) => void;
  gpt_metrics: ({
    metric_id: string;
  } & IndividualGptMetricType)[];
};

// type SettingsMetricStep = string;

const chartSvgData: Record<ReportChartGraphType | 'number', IconNameType> = {
  barchart: 'BarChartIcon',
  linechart: 'LineChartIcon',
  horizontal_barchart: 'HorizontalBarChartIcon',
  piechart: 'PieChartIcon',
  columnchart: 'ColumnChartIcon',
  heatMap: 'HeatmapChartIcon',
  donut: 'DonutChartIcon',
  areaCharts: 'AreaChartIcon',
  radialBars: 'RadialBarChartIcon',
  radialCharts: 'RadialChartIcon',
  number: 'NumberChartIcon',
};
const DEFAULT_TIME_START = '00:00';
const DEFAULT_TIME_END = '23:59';
const SettingsFormBlock: FC<SettingsFormBlockProps> = (props) => {
  const { id } = useParams();
  const { t } = useTranslation('pages.chartPage');
  const {
    dateIntervals,
    dateToString,
    intervalKeysMapNewChartSettingsFormat,
    dateToStringNewFormat,
    intervalTimeBuilder,
  } = useChartDateIntervals();
  const dispatch = useAppDispatch();
  const [dateInterval, setDateInterval] = useState({
    start: moment().format('DD.MM.yyyy'),
    end: moment().format('DD.MM.yyyy'),
    endTime: DEFAULT_TIME_START,
    startTime: DEFAULT_TIME_END,
  });
  const { charts: voiceCharts } = useAppSelector((state) => state.reportCharts);
  const { charts: chatCharts } = useAppSelector((state) => state.chatReportCharts);
  const charts = useMemo(
    () => ({ chat: chatCharts, voice: voiceCharts }),
    [chatCharts, voiceCharts],
  );
  //css
  const fieldHintClassName =
    'leading-[110%] absolute -bottom-[35px] text-[12px] text-3color w-[230px]';
  const valuesLabelPathClassName =
    'text-[15px] font-[500] leading-[16.4px] tracking-tight text-0color whitespace-nowrap';
  const fieldAndLabelBlock = 'grid grid-cols-[160px_1fr] items-center w-full';
  const fieldAndLabelBlockForAdditionalBlock =
    'grid grid-cols-[250px_fit-content(500px)] items-center w-full';
  const firstBlockValueWidth = 'w-[220px]';
  const settingsNavStyles = (active = false) =>
    cn(
      'flex items-center cursor-pointer hover:text-1color w-full justify-center pl-[18px] pr-[22px] pt-[12px] pb-[10px] text-base text-2color',
      active
        ? 'bg-gradient-to-b from-white to-gray rounded-t-[10px]  cursor-default'
        : 'cursor-pointer',
    );
  const { setup, onClose, isOpen, onRemove, gpt_metrics } = props;
  const [activeMenuItem, changeActiveMenuItem] = useState<'main' | 'axis' | 'additional'>('main');
  const [metricReverseColorMode, setColorMode] = useState<string>(
    (setup?.settings as NewLiveReportGraphDataSettingsDataBaseCharts)?.group?.color?.length === 1
      ? 'singleColor'
      : 'manyColors',
  );
  //segmented date interval feature
  const [dateIntervalFirstPart, setDateIntervalFirstPart] = useState<
    'last' | 'current' | 'prev' | 'customDate' | 'all'
  >('last');

  const [dateIntervalSecondPart, setDateIntervalSecondPart] = useState<
    '1' | '7' | '30' | 'quarter' | undefined
  >('7');

  const initializedRef = useRef(false);

  useEffect(() => {
    if (initializedRef.current) return;

    const range = setup?.settings.range;

    if (!range) return;
    const reportRange = range as ReportTimeRange;
    if (reportRange.type === 'r') {
      const times = reportRange.parameters?.reduce<Record<'start' | 'end', string | undefined>>(
        (acc, cur) => {
          if (cur.condition === '>') {
            acc['start'] = cur.value.split(' ')[0];
          } else {
            acc['end'] = cur.value.split(' ')[0];
          }
          return acc;
        },
        { start: undefined, end: undefined },
      );
      setDateInterval({
        start: moment(times?.start, 'YYYY-MM-DD').format('DD.MM.yyyy'),
        end: moment(times?.end, 'YYYY-MM-DD').format('DD.MM.yyyy'),
        startTime: DEFAULT_TIME_START,
        endTime: DEFAULT_TIME_END,
      });
    }
    initializedRef.current = true;
    const { first, second } = dateToStringNewFormat(range);

    setDateIntervalFirstPart(first);
    setDateIntervalSecondPart(second);
  }, [dateToStringNewFormat, setup]);

  const dateIntervalFirstPartOptions = [
    { title: t('last'), value: 'last' },
    { title: t('current'), value: 'current' },
    { title: t('prev'), value: 'prev' },
    { title: t('select.custom_date'), value: 'customDate' },
    { title: t('select.all_data'), value: 'all' },
  ];
  // eslint-disable-next-line
  const dateIntervalSecondPartOptionsLast = [
    { title: t('dayInHour'), value: '1' },
    { title: t('select.weekdays'), value: '7' },
    { title: t('select.monthDays'), value: '30' },
  ];
  const dateIntervalSecondPartOptionsCurrentAndPrev = [
    { title: t('day'), value: '1' },
    { title: t('week'), value: '7' },
    { title: t('month'), value: '30' },
    { title: t('quarter'), value: 'quarter' },
  ];
  //segmented date interval feature

  const { metricList, currentTemplate } = useOutletContext<ReportOutletContext>();

  const [createChartVoice] = useAddNewLiveReportGraphMutation();
  const [editChartVoice] = useEditNewLiveReportGraphMutation();
  const [createChartChat] = useAddNewChatLiveReportGraphMutation();
  const [editChartChat] = useEditNewChatLiveReportGraphMutation();

  const createChart = { chat: createChartChat, voice: createChartVoice };
  const editChart = { chat: editChartChat, voice: editChartVoice };

  // const reportsTimeIntervals = [...daysSelectOptions];
  const submitFormRef = useRef<HTMLInputElement>(null);
  // constants
  const aggregateSelectOptions = [
    { title: t('aggregate.mid'), value: 'avg' },
    {
      title: t('aggregate.sum'),
      value: 'sum',
    },
    {
      title: t('aggregate.count'),
      value: 'count',
    },
    {
      title: t('aggregate.min'),
      value: 'min',
    },
    {
      title: t('aggregate.max'),
      value: 'max',
    },
    {
      title: t('aggregate.prop_num'),
      value: 'prop_num',
    },
  ];
  const dateSelectOptions = [
    { title: t('date_select.h'), value: 'h' },
    { title: t('date_select.d'), value: 'd' },
    { title: t('date_select.w'), value: 'w' },
    { title: t('date_select.m'), value: 'm' },
  ];

  const defaultColorValue = '#c592f7';
  const chartDataDefaultValue = {
    metric_id: metricList?.[0].metric_id || '',
    aggregate: metricIsString(metricList?.[0].metric_id || '')
      ? (aggregateSelectOptions.filter(
          (item) => item.value === 'count' || item.value === 'prop_num',
        )[0].value as AggregateSelectForm)
      : (aggregateSelectOptions[0].value as AggregateSelectForm),
    color: [defaultColorValue],
    type: 'column',
    conditional_formatting: [],
  };

  const settingsNavMenuData = [
    { name: t('settings'), value: 'main' },
    { name: t('axisSettings'), value: 'axis' },
    { name: t('additionalSettings'), value: 'additional' },
  ];
  const getLayoutSetup = () => {
    const maxAvailableWidth = 24;
    const defaultHeight = 6;
    const defaultWidth = 12;
    if (!charts[currentTemplate || 'voice']?.length) {
      return { w: defaultWidth, h: defaultHeight, x: 0, y: 0 };
    }
    const layoutsData = charts[currentTemplate || 'voice']?.map((chart) => chart.layout);
    const yMaxIntoLayout = Math.max(...(layoutsData?.map((layout) => layout.y) || []));
    const xMaxIntoLayout = Math.max(
      ...(layoutsData?.filter((layout) => layout.y === yMaxIntoLayout).map((layout) => layout.x) ||
        []),
    );
    const layoutFromYMaxInLayout = layoutsData?.find(
      (layout) => layout.x === xMaxIntoLayout && layout.y === yMaxIntoLayout,
    ) as ChartsLayoutType;
    const widthCalculations =
      maxAvailableWidth - (layoutFromYMaxInLayout.x + layoutFromYMaxInLayout.w);
    return {
      w: widthCalculations >= 6 ? (widthCalculations < 0 ? 0 : widthCalculations) : defaultWidth,
      h: defaultHeight,
      x: widthCalculations >= 6 ? layoutFromYMaxInLayout.x + layoutFromYMaxInLayout.w : 0,
      y:
        widthCalculations >= 6
          ? yMaxIntoLayout
          : yMaxIntoLayout === 0
          ? defaultHeight
          : yMaxIntoLayout + 1,
    };
  };
  // hookForm
  const methods = useForm<CreateNewReportGraphType>({
    defaultValues:
      setup?.settings.type === 'number'
        ? {
            name: setup?.name || '',
            live_report_id: id || '',
            layout: setup?.layout,
            settings: {
              custom_properties: {
                ...(setup?.settings?.custom_properties || {}),
                x_axis_show:
                  typeof setup?.settings?.custom_properties?.x_axis_show === 'undefined'
                    ? true
                    : setup?.settings?.custom_properties?.x_axis_show,
                y_axis_show:
                  typeof setup?.settings?.custom_properties?.y_axis_show === 'undefined'
                    ? true
                    : setup?.settings?.custom_properties?.y_axis_show,
              },
              comparison: setup?.settings.comparison || { enabled: false, mode: 'percent' },
              data: setup?.settings.data || [chartDataDefaultValue],
              range: dateToString(setup?.settings.range) || 'week',
              styles: setup?.settings.styles || {
                header_size: 18,
                text_size: 136,
                text_color: '#000',
                comparison_font_size: 28,
                comparison_positive_color: '#A3C585',
                comparison_negative_color: '#E36144',
              },
              type: setup?.settings.type || 'number',
              filter: setup?.settings.filter,
            } as NewLiveReportGraphDataSettingsDataNumberChart,
          }
        : {
            name: setup?.name || '',
            live_report_id: id || '',
            layout: setup?.layout,
            settings: {
              custom_properties: {
                ...(setup?.settings?.custom_properties || {}),
                x_axis_show:
                  typeof setup?.settings?.custom_properties?.x_axis_show === 'undefined'
                    ? true
                    : setup?.settings?.custom_properties?.x_axis_show,
                y_axis_show:
                  typeof setup?.settings?.custom_properties?.y_axis_show === 'undefined'
                    ? true
                    : setup?.settings?.custom_properties?.y_axis_show,
              },
              max_value_limit: setup?.settings.max_value_limit,
              display_data: setup?.settings.display_data || false,
              yaxis_min_value: setup?.settings.yaxis_min_value || false,
              display_legend: setup?.settings.display_legend || false,
              empty_values: setup?.settings.empty_values || false,
              nullable_values: setup?.settings.nullable_values || false,
              max_group_display: setup?.settings.max_group_display,
              order_by: setup?.settings.order_by || 'none',
              type: setup?.settings.type || 'barchart',
              range: dateToString(setup?.settings.range) || 'week',
              data: setup?.settings.data || [chartDataDefaultValue],
              filter: setup?.settings.filter,
              group: setup?.settings.group || {
                type: 'date',
                step: dateSelectOptions[0].value as SettingsDateStep,
                metric_id: metricList?.[0].metric_id,
                color:
                  setup?.settings.group.type === 'metric_reverse'
                    ? setup?.settings.group.color
                    : undefined,
              },
              line_settings: setup?.settings.line_settings || {
                value: [1],
                color: defaultColorValue,
                active: false,
              },
              styles: setup?.settings.styles || {
                grid: false,
                header_size: 18,
                legend_position: 'bottom',
                value_position: 'outside',
                text_size: 12,
                text_color: '#000',
              },
            },
          },
  });

  const { register, watch, reset, control, setValue, handleSubmit } = methods;
  //chartDataFields
  const {
    fields: chartDataFields,
    append: appendChartDataField,
    remove: removeChartDataField,
  } = useFieldArray({
    name: 'settings.data',
    control,
  });
  //conditionalFormattingFields
  // handlers
  const splitMetrics = [...(metricList || []), ...(gpt_metrics || [])];
  function getSelectOptionsFromMetrics() {
    return (
      splitMetrics?.map((metric) => ({
        title: metric.name,
        value: metric?.metric_id,
      })) || []
    );
  }

  const metricOptions = getSelectOptionsFromMetrics();

  function metricIsString(id: string | string[]): boolean {
    if (Array.isArray(id)) {
      for (const elem of id) {
        const type = metricList?.find((metric) => metric.metric_id === elem)?.result_value_type;
        if (type === 'str') return true;
      }
      return false;
    }
    if (id) {
      return metricList?.find((metric) => metric.metric_id === id)?.result_value_type === 'str';
    } else {
      return (
        metricList?.find((metric) => metric.metric_id === metricList[0]?.metric_id)
          ?.result_value_type === 'str'
      );
    }
  }
  function handlerRemove() {
    onRemove?.(true);
  }
  function handlerSuccess() {
    submitFormRef?.current?.click();
  }

  function rangeFormatter() {
    if (dateIntervalFirstPart === 'all') {
      return dateIntervals['all'];
    }
    if (dateIntervalFirstPart === 'customDate') {
      return {
        type: 'r',
        parameters: [
          {
            value:
              moment(dateInterval?.start, 'DD.MM.yyyy').format('YYYY-MM-DD') +
              ` ${dateInterval.startTime}`,
            condition: '>',
          },
          {
            value:
              moment(dateInterval?.end, 'DD.MM.yyyy').format('YYYY-MM-DD') +
              ` ${dateInterval.endTime}`,
            condition: '<',
          },
        ],
      };
      // return watch('settings.range');
    }
    if (dateIntervalFirstPart && dateIntervalSecondPart) {
      const intervalKey =
        intervalKeysMapNewChartSettingsFormat?.[dateIntervalFirstPart]?.[dateIntervalSecondPart];
      return dateIntervals[intervalKey];
    }
    return dateIntervals['all'];
  }

  function onSubmitForm(data: CreateNewReportGraphType) {
    dispatch(changeChartLoading(setup?.graph_id || null));
    let formattedData: CreateNewReportGraphType;
    const range = rangeFormatter();
    if (data.settings.type === 'number') {
      formattedData = {
        name: data?.name || '',
        live_report_id: id || '',
        layout: data.layout ? data.layout : (getLayoutSetup() as ChartsLayoutType),
        settings: {
          comparison: data?.settings.comparison,
          data: data?.settings.data,
          range,
          styles: data?.settings.styles,
          type: 'number',

          filter: data?.settings.filter,
        },
      };
    } else {
      formattedData = {
        ...data,
        layout: data.layout ? data.layout : (getLayoutSetup() as ChartsLayoutType),
        settings: {
          ...data.settings,
          line_settings: data.settings.line_settings,
          max_value_limit: data.settings.max_value_limit || undefined,
          // line_settings: data.settings.line_settings.active ? data.settings.line_settings : {},
          group:
            data.settings.group.type === 'metric'
              ? { type: 'metric', metric_id: data.settings.group.metric_id }
              : data.settings.group.type === 'metric_reverse'
              ? {
                  type: 'metric_reverse',
                  metric_id: data.settings.group.metric_id,
                  color: data.settings.group.color,
                }
              : { type: 'date', step: data.settings.group.step },
          max_group_display: !data.settings.max_group_display
            ? undefined
            : data.settings.max_group_display,
          range,
          data: (data.settings.group.type === 'metric_reverse'
            ? data.settings.data.slice(0, 1).map((i) => ({ ...i, conditional_formatting: [] }))
            : data.settings.data
          ).map((axis) => {
            return {
              ...axis,
              conditional_formatting:
                data.settings.type === 'columnchart' ||
                data.settings.type === 'piechart' ||
                data.settings.type === 'donut' ||
                data.settings.type === 'linechart'
                  ? []
                  : axis.conditional_formatting,
            };
          }),
        },
      };
    }
    if (!formattedData) return;

    if (!setup) {
      createChart[currentTemplate || 'voice'](formattedData).then(() => onClose(false));
    } else {
      delete formattedData.live_report_id;
      editChart[currentTemplate || 'voice']({
        params: { id: setup.graph_id },
        body: formattedData,
        liveReportId: data.live_report_id,
      }).then(() => onClose(false));
    }
    reset();
    setTimeout(() => dispatch(changeChartLoading(null)), 400);
  }

  useEffect(() => {
    changeActiveMenuItem('main');
  }, [isOpen]);

  const { conditions } = UniversalFilterConstants();

  function getConditionsSelectOptions(value?: string) {
    const alias = splitMetrics.find((metric) => metric.metric_id === value);
    if (value && alias && alias.result_value_type) {
      return (
        conditions
          .filter((condition) => !condition.metricOnly)
          // .filter((condition) => condition.type_meta.includes(alias?.type_meta as string))
          .filter(
            (condition) =>
              alias.result_value_type && condition.values.includes(alias.result_value_type),
          )
          .map((item) => ({ title: item.name, value: item.id }))
      );
    } else
      return conditions
        .filter((condition) => !condition.aliasOnly)
        .filter(
          (condition) =>
            alias?.result_value_type && condition.values.includes(alias.result_value_type),
        )
        .map((item) => ({ title: item.name, value: item.id }));
  }

  // jsx markup data
  const renderSortDataSelect = {
    metric: (
      <div className="w-[455px]">
        <Select
          withSearch
          defaultValue={watch('settings.group.metric_id') ?? undefined}
          onChange={(value) => setValue('settings.group.metric_id', value as string)}
          options={metricOptions}
          dropWidth={576}
        />
      </div>
    ),
    metric_reverse: (
      <div className="w-full min-w-0">
        <Select
          withSearch
          isMulti
          onChangeMultiple={(values) => setValue('settings.group.metric_id', values as string[])}
          defaultValue={
            Array.isArray(watch('settings.group.metric_id'))
              ? watch('settings.group.metric_id')
              : []
          }
          options={metricOptions}
          dropWidth={576}
        />
      </div>
    ),
    date: (
      <div className="w-[260px]">
        <SegmentedControl
          valuesList={dateSelectOptions}
          isActive={watch('settings.group.step')}
          onChange={(value) => setValue('settings.group.step', value as SettingsDateStep)}
        />
      </div>
    ),
  };
  const aggregateSelectOptionsFiltered = (function getAggregateSelect() {
    return watch('settings.type') === 'number'
      ? [...aggregateSelectOptions.slice(0, 1), ...aggregateSelectOptions.slice(2)]
      : aggregateSelectOptions;
  })();

  function handleGenerateColorsForData() {
    const length = chartDataFields.length;
    const colors = generateColors(length, 'array');
    const formattedData = watch('settings.data').map((field, index) => ({
      ...field,
      color: [colors[index]],
    }));
    setValue('settings.data', formattedData);
  }

  function handleChangeChartIntervalType(val) {
    if (val === 'all') {
      setDateIntervalSecondPart(undefined);
    }
    setDateIntervalFirstPart(val as 'last');
  }

  async function handlerChangeCustomRangeInterval({
    start,
    end,
  }: {
    start: string | null | Date;
    end: string | null | Date;
  }) {
    const range = intervalTimeBuilder(start, end) as unknown as ReportTimeRange;
    setValue('settings.range', range);
  }

  const renderMenuItems = {
    main: (
      <div>
        <div className="w-full">
          <div className="flex items-start pl-[25px] flex-col gap-[25px]">
            <div className="flex justify-between w-full gap-[20px]">
              <div className="flex items-start flex-col gap-[25px] w-full">
                <div className={fieldAndLabelBlock}>
                  <span className={valuesLabelPathClassName}>{t('chart_name')}</span>
                  <div>
                    {/*name*/}
                    <Input {...register('name')} />
                  </div>
                </div>
                <div className={fieldAndLabelBlock}>
                  <span className={valuesLabelPathClassName}>{t('diagram_type')}</span>
                  {/*diagram_type*/}
                  <div>
                    <Select
                      defaultValue={String(watch('settings.type'))}
                      dropWidth={609}
                      onChange={(value) => {
                        if (value === 'number') {
                          setValue('settings.styles.text_size', 136);
                        } else {
                          setValue('settings.styles.text_size', 12);
                        }
                        setValue('settings.type', value as ReportChartGraphType);
                      }}
                      options={chartsTypeData.map((item) => ({
                        title: t(item.name),
                        value: item.type,
                      }))}
                      tooltip={{
                        place: 'left',
                        content:
                          t(
                            chartsTypeData?.find((chart) => chart.type === watch(`settings.type`))
                              ?.name || chartsTypeData?.[0]?.name,
                          ) || '',
                        id: 'chartType',
                      }}
                    />
                  </div>
                </div>
              </div>
              {/*image graph*/}
              <div className="px-[22px] py-[11px] bg-light_gray border-[1px] rounded-[12px] border-[#e4e5e8]">
                <Icon name={chartSvgData[watch('settings.type') || 'barchart']} size={77} />
              </div>
              {/*image graph*/}
            </div>
            <div className={fieldAndLabelBlock}>
              <span className={valuesLabelPathClassName}>{t('default')}</span>
              <div className="grid grid-cols-[1fr_310px] gap-x-[10px]">
                <SegmentedControl
                  valuesList={dateIntervalFirstPartOptions}
                  isActive={dateIntervalFirstPart}
                  onChange={handleChangeChartIntervalType}
                />
                {dateIntervalFirstPart === 'customDate' && (
                  <div className="flex items-center gap-[2px]">
                    <div>{t('range_interval_from')}</div>
                    <CustomDatePicker
                      inputClassName="!w-full"
                      dataId={'reports-chart-settings-interval-date-from'}
                      selected={moment(dateInterval.start, 'DD.MM.yyyy').toDate()}
                      //
                      onChange={(date) => {
                        setDateInterval((prev) => ({ ...prev, start: date }));
                        handlerChangeCustomRangeInterval({
                          start: `${moment(date, 'DD.MM.yyyy').format('YYYY-MM-DD')} ${
                            dateInterval.startTime
                          }:00`,
                          end: `${moment(dateInterval.end, 'DD.MM.yyyy').format('YYYY-MM-DD')} ${
                            dateInterval.endTime
                          }:00`,
                        });
                      }}
                      maxDate={moment(dateInterval.end, 'DD.MM.yyyy').toDate()}
                      minDate={moment(dateInterval.end, 'DD.MM.yyyy')
                        .subtract(364, 'days')
                        .toDate()}
                    />
                    <div>{t('range_interval_to')}</div>
                    <CustomDatePicker
                      inputClassName="!w-full"
                      dataId={'reports-chart-settings-interval-date-to'}
                      selected={moment(dateInterval.end, 'DD.MM.yyyy').toDate()}
                      //
                      onChange={(date) => {
                        setDateInterval((prev) => ({ ...prev, end: date }));
                        handlerChangeCustomRangeInterval({
                          start: `${moment(dateInterval.start, 'DD.MM.yyyy').format(
                            'YYYY-MM-DD',
                          )} ${dateInterval.startTime}:00`,
                          end: `${moment(date, 'DD.MM.yyyy').format('YYYY-MM-DD')} ${
                            dateInterval.endTime
                          }:00`,
                        });
                      }}
                      minDate={moment(dateInterval.start, 'DD.MM.yyyy').toDate()}
                      maxDate={moment(dateInterval.start, 'DD.MM.yyyy')
                        .subtract(-364, 'days')
                        .toDate()}
                    />
                  </div>
                )}
                {dateIntervalFirstPart !== 'all' && dateIntervalFirstPart !== 'customDate' && (
                  <SegmentedControl
                    valuesList={
                      dateIntervalFirstPart === 'last'
                        ? dateIntervalSecondPartOptionsLast
                        : dateIntervalSecondPartOptionsCurrentAndPrev
                    }
                    isActive={dateIntervalSecondPart || ''}
                    onChange={(val) => setDateIntervalSecondPart(val as '1')}
                  />
                )}
                {/*range*/}
              </div>
            </div>

            {watch('settings.type') !== 'columnchart' &&
              watch('settings.type') !== 'number' &&
              watch('settings.type') !== 'piechart' &&
              watch('settings.type') !== 'donut' && (
                <div className={fieldAndLabelBlock}>
                  <span className={valuesLabelPathClassName}>{t('order_by')}</span>
                  <div className="w-[350px]">
                    {/*order_by*/}
                    <SegmentedControl
                      valuesList={[
                        { title: t('order_by_options.asc'), value: 'asc' },
                        { title: t('order_by_options.desc'), value: 'desc' },
                        { title: t('order_by_options.none'), value: 'none' },
                      ]}
                      isActive={watch('settings.order_by')}
                      onChange={(value) =>
                        setValue('settings.order_by', value as OrderByChartSettings)
                      }
                    />
                  </div>
                </div>
              )}
            {watch('settings.type') === 'number' ? (
              <div className={fieldAndLabelBlock}>
                <span className={valuesLabelPathClassName}>{t('num_font')}</span>
                <div className="grid grid-cols-[110px_50px] gap-x-[10px] items-center ">
                  <Input
                    iconColor="primary"
                    onClick={() => setValue('settings.styles.text_size', 136)}
                    icon="CancelIcon"
                    type="number"
                    {...register('settings.styles.text_size')}
                  />
                  <ColorInput
                    color={watch('settings.styles.text_color')}
                    changeColor={(color) => setValue('settings.styles.text_color', color)}
                  />
                </div>
              </div>
            ) : (
              <div className={fieldAndLabelBlock}>
                <span className={valuesLabelPathClassName}>{t('categories_len')}</span>
                <div className="w-[120px] relative">
                  <Input
                    {...register('settings.custom_properties.chart_categories_length', {
                      setValueAs: (value) => (value === '' ? undefined : Number(value)),
                    })}
                    type="number"
                  />
                  <span className={fieldHintClassName}>{t('categories_len_hint')}</span>
                </div>
              </div>
            )}
            {/* TODO: separate there  */}
            <div className="py-[20px] w-full h-[0px] ">
              <div className=" border-[0.5px] border-b_medium" />
            </div>
            {/* TODO: separate there  */}
            <h3 className="text-[18px] text-1color font-bold">{t('display')}</h3>
            {watch('settings.type') === 'number' && (
              <>
                {watch('settings.type') === 'number' && (
                  <div className="grid grid-cols-[350px_1fr] items-center w-full">
                    <div className={valuesLabelPathClassName}>{t('comparison_enabled')}</div>
                    <div className={firstBlockValueWidth}>
                      <Toggle
                        checked={watch('settings.comparison.enabled')}
                        onChecked={(status) => setValue('settings.comparison.enabled', status)}
                        disabled={dateIntervalFirstPart === 'all'}
                      />
                    </div>
                  </div>
                )}
                {watch('settings.comparison.enabled') && (
                  <div className={'grid grid-cols-[350px_1fr] items-center w-full'}>
                    <span className={valuesLabelPathClassName}>{t('comparison_mode')}</span>
                    <div className={firstBlockValueWidth}>
                      <Select
                        defaultValue={watch('settings.comparison.mode')}
                        onChange={(val) =>
                          setValue('settings.comparison.mode', val as 'percent' | 'absolute')
                        }
                        options={[
                          { value: 'percent', title: t('comparison_mode_percent') },
                          { value: 'absolute', title: t('comparison_mode_absolute') },
                        ]}
                      />
                    </div>
                  </div>
                )}
                {watch('settings.comparison.enabled') && (
                  <div className={'grid grid-cols-[350px_1fr] items-center w-full'}>
                    <span className={valuesLabelPathClassName}>{t('comparison_font_size')}</span>
                    <div className={firstBlockValueWidth}>
                      <Input {...register('settings.styles.comparison_font_size')} type="number" />
                    </div>
                  </div>
                )}
                {watch('settings.comparison.enabled') && (
                  <div className={'grid grid-cols-[350px_1fr] items-center w-full'}>
                    <span className={valuesLabelPathClassName}>
                      {t('comparison_positive_color')}
                    </span>
                    <div className={cn(firstBlockValueWidth, 'h-full flex items-center')}>
                      <ColorInput
                        color={watch('settings.styles.comparison_positive_color')}
                        changeColor={(color) =>
                          setValue('settings.styles.comparison_positive_color', color)
                        }
                        label={t('select.color_choices')}
                      />
                    </div>
                  </div>
                )}
                {watch('settings.comparison.enabled') && (
                  <div className={'grid grid-cols-[350px_1fr] items-center w-full'}>
                    <span className={valuesLabelPathClassName}>
                      {t('comparison_negative_color')}
                    </span>
                    <div className={cn(firstBlockValueWidth, 'h-full flex items-center')}>
                      <ColorInput
                        color={watch('settings.styles.comparison_negative_color')}
                        changeColor={(color) =>
                          setValue('settings.styles.comparison_negative_color', color)
                        }
                        label={t('select.color_choices')}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
            {watch('settings.type') !== 'number' && (
              <div className={fieldAndLabelBlock + ' gap-[65px]'}>
                <span className={valuesLabelPathClassName}>{t('display_values')}</span>
                <div className="flex items-center gap-[30px]">
                  {/*display_data*/}
                  <Toggle
                    checked={watch('settings.display_data')}
                    onChecked={(status) => setValue('settings.display_data', status)}
                  />
                  {watch('settings.display_data') && (
                    <>
                      <div className="w-[160px]">
                        <SegmentedControl
                          isActive={watch('settings.styles.value_position')}
                          onChange={(value) =>
                            setValue(
                              'settings.styles.value_position',
                              value as 'inside' | 'outside',
                            )
                          }
                          valuesList={[
                            { title: t('value_positions_options.inside'), value: 'inside' },
                            { title: t('value_positions_options.outside'), value: 'outside' },
                          ]}
                        />
                      </div>
                      <div className="grid grid-cols-[auto_90px_1fr] items-center pl-[30px] gap-x-[10px]">
                        <span className={valuesLabelPathClassName}>{t('font')}:</span>
                        <Input
                          iconColor="primary"
                          onClick={() => setValue('settings.styles.text_size', 12)}
                          icon="CancelIcon"
                          type="number"
                          {...register('settings.styles.text_size')}
                        />
                        <ColorInput
                          color={watch('settings.styles.text_color')}
                          changeColor={(color) => setValue('settings.styles.text_color', color)}
                        />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
            {watch('settings.type') !== 'number' && (
              <div className={fieldAndLabelBlock + ' gap-[65px]'}>
                <span className={valuesLabelPathClassName}>{t('display_legend')}</span>
                <div className={'flex items-center gap-[30px]'}>
                  {/*display_legend*/}
                  <Toggle
                    checked={watch('settings.display_legend')}
                    onChecked={(status) => setValue('settings.display_legend', status)}
                  />
                  {watch('settings.display_legend') && (
                    <div className="w-[510px]">
                      <SegmentedControl
                        valuesList={[
                          { title: t('legend_position_options.bottom'), value: 'bottom' },
                          { title: t('legend_position_options.left'), value: 'left' },
                          {
                            title: t('legend_position_options.right'),
                            value: 'right',
                            icon: 'ArrowBackwardSquare',
                          },
                        ]}
                        isActive={watch('settings.styles.legend_position')}
                        onChange={(value) =>
                          setValue(
                            'settings.styles.legend_position',
                            value as 'bottom' | 'left' | 'right',
                          )
                        }
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
            {watch('settings.type') !== 'number' && (
              <div className={fieldAndLabelBlock + ' gap-[65px]'}>
                <span className={valuesLabelPathClassName}>{t('empty_values')}</span>
                <div className={firstBlockValueWidth}>
                  {/*empty_values*/}
                  <Toggle
                    checked={watch('settings.empty_values')}
                    onChecked={(status) => setValue('settings.empty_values', status)}
                  />
                </div>
              </div>
            )}
            {watch('settings.type') !== 'columnchart' &&
              watch('settings.type') !== 'piechart' &&
              watch('settings.type') !== 'donut' &&
              watch('settings.type') !== 'number' &&
              watch('settings.type') !== 'linechart' && (
                <div className={fieldAndLabelBlock + ' gap-[65px]'}>
                  <span className={valuesLabelPathClassName}>{t('nullable_values')}</span>
                  <div className={firstBlockValueWidth}>
                    {/*nullable_values*/}
                    <Toggle
                      checked={watch('settings.nullable_values')}
                      onChecked={(status) => setValue('settings.nullable_values', status)}
                    />
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    ),
    axis: (
      <>
        {watch('settings.type') !== 'number' && (
          <div className=" w-[860px] flex flex-col gap-[20px]">
            <div className="text-[18px] text-1color font-bold">{t('data_hor_x')}</div>
            <div className="flex items-start pl-[25px] flex-col gap-[25px]">
              <div className={fieldAndLabelBlockForAdditionalBlock}>
                <span className={valuesLabelPathClassName}>{t('data_x')}</span>
                <div className="w-[455px]">
                  {/*settings.group.type*/}
                  <SegmentedControl
                    valuesList={[
                      { title: t('date_call'), value: 'date' },
                      { title: t('metric'), value: 'metric' },
                      { title: t('metric_reverse_categories'), value: 'metric_reverse' },
                    ]}
                    isActive={watch('settings.group.type')}
                    onChange={(value) => {
                      if (value === 'metric_reverse') {
                        setValue('settings.group.metric_id', []);
                        setValue('settings.data', [chartDataDefaultValue]);
                      } else {
                        setValue('settings.group.metric_id', undefined);
                      }
                      setValue(
                        'settings.group.type',
                        value as 'date' | 'metric' | 'metric_reverse',
                      );
                    }}
                  />
                </div>
              </div>
              <div className={fieldAndLabelBlockForAdditionalBlock + ' !grid-cols-[250px_auto]'}>
                <span className={valuesLabelPathClassName}>{t('choose_category')}</span>
                {watch('settings.group.type')
                  ? renderSortDataSelect[watch('settings.group.type')]
                  : renderSortDataSelect['date']}
              </div>
              {watch('settings.group.type') === 'metric_reverse' && (
                <div className={fieldAndLabelBlockForAdditionalBlock + ' !items-baseline'}>
                  <span className={valuesLabelPathClassName}>{t('color')}</span>
                  <div className="grid grid-cols-1 grid-rows-2 gap-y-[20px]">
                    <div className="w-[250px]">
                      <SegmentedControl
                        valuesList={[
                          { title: t('palette'), value: 'manyColors' },
                          { title: t('single_color'), value: 'singleColor' },
                        ]}
                        isActive={metricReverseColorMode}
                        onChange={(colorMode) => {
                          if (colorMode === 'singleColor') {
                            const color = generateColors(1, 'array');
                            setValue('settings.group.color', color);
                          }
                          setColorMode(colorMode);
                        }}
                      />
                    </div>
                    {metricReverseColorMode === 'singleColor' ? (
                      <div className="flex items-center gap-[20px]">
                        <ColorInput
                          color={watch('settings.group.color')?.[0] || '#000000'}
                          changeColor={(val) => setValue('settings.group.color', [val])}
                        />
                        <div
                          data-drop-menu-ignore
                          onClick={() => {
                            const color = generateColors(1, 'array');
                            setValue('settings.group.color', color);
                          }}
                          className="flex items-center gap-[10px] cursor-pointer hover:opacity-90"
                        >
                          <Icon size={15} className="text-action" name="RandomIcon" />
                          <span className="text-action text-[15px] font-medium">
                            {t('generate')}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <PieChartPalette
                        defaultValue={watch('settings.group.color') || []}
                        onChange={(val) => setValue('settings.group.color', val)}
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        <div className={`${watch('settings.type') === 'number' ? '' : 'pt-[38px]'}`}>
          {chartDataFields.map((dataField, dataIndex) => (
            <div className="pb-[38px] flex flex-col gap-[20px]" key={dataField.id}>
              <div className={fieldAndLabelBlockForAdditionalBlock}>
                <div className="text-[18px] text-1color font-bold">
                  {t('data_settings', { index: dataIndex + 1 })}
                </div>
                <div className="flex items-center pl-[25px] w-[525px]">
                  {watch('settings.type') !== 'piechart' &&
                    watch('settings.type') !== 'donut' &&
                    chartDataFields.length - 1 === dataIndex &&
                    watch('settings.type') !== 'number' &&
                    watch('settings.group.type') !== 'metric_reverse' && (
                      <div>
                        <Button
                          label={t('add_row')}
                          fill="outlined"
                          onClick={() => appendChartDataField(chartDataDefaultValue)}
                          icon="CirclePlusIcon"
                          sizeIcon={13}
                        />
                      </div>
                    )}
                  {chartDataFields.length > 1 && (
                    <div>
                      <CircleButton
                        onClick={() => removeChartDataField(dataIndex)}
                        size={16}
                        className="text-basic_red"
                        icon="TrashIcon"
                      />
                    </div>
                  )}
                  {dataIndex === 0 &&
                    watch('settings.type') !== 'piechart' &&
                    watch('settings.type') !== 'number' &&
                    watch('settings.type') !== 'donut' &&
                    watch('settings.group.type') !== 'metric_reverse' && (
                      <div className="ml-auto">
                        <div
                          onClick={handleGenerateColorsForData}
                          className="flex items-center gap-[10px] cursor-pointer hover:opacity-90"
                        >
                          <Icon size={15} className="text-action" name="RandomIcon" />
                          <span className="text-action text-[15px] font-medium">
                            {t('generate_colors_column')}
                          </span>
                        </div>
                      </div>
                    )}
                </div>
              </div>
              <div className="flex items-start pl-[25px] flex-col gap-[25px]">
                <div className={fieldAndLabelBlockForAdditionalBlock}>
                  <span className={valuesLabelPathClassName}>{t('labels.metric_axis')}</span>
                  <div className="flex items-center w-[500px] gap-[6px]">
                    <Select
                      withSearch
                      defaultValue={watch(`settings.data.${dataIndex}.metric_id`)}
                      onChange={(value) =>
                        setValue(`settings.data.${dataIndex}.metric_id`, value as string)
                      }
                      options={metricOptions}
                      dropWidth={468}
                      tooltip={{
                        place: 'left',
                        content:
                          metricList?.find(
                            (metric) =>
                              metric.metric_id === watch(`settings.data.${dataIndex}.metric_id`),
                          )?.name ||
                          metricList?.[0]?.name ||
                          '',
                        id: `metric_axis_y2-${dataField.id}`,
                      }}
                    />
                    {watch('settings.type') !== 'piechart' &&
                      watch('settings.type') !== 'donut' &&
                      watch('settings.type') !== 'number' &&
                      watch('settings.group.type') !== 'metric_reverse' && (
                        <ColorInput
                          color={watch(`settings.data.${dataIndex}.color.0`)}
                          changeColor={(color) =>
                            setValue(`settings.data.${dataIndex}.color.0`, color)
                          }
                        />
                      )}
                  </div>
                </div>
                <div className={fieldAndLabelBlockForAdditionalBlock}>
                  <span className={valuesLabelPathClassName}>{t('labels.aggregate_data')}</span>
                  <div
                    className={`${
                      metricIsString(
                        watch('settings.group.type') === 'metric_reverse'
                          ? [
                              ...(watch('settings.group.metric_id') || []),
                              watch(`settings.data.${dataIndex}.metric_id`),
                            ]
                          : watch(`settings.data.${dataIndex}.metric_id`),
                      )
                        ? 'w-[200px]'
                        : 'w-[500px]'
                    }`}
                  >
                    <SegmentedControl
                      valuesList={(() => {
                        const metricId =
                          watch('settings.group.type') === 'metric_reverse'
                            ? [
                                ...(watch('settings.group.metric_id') || []),
                                watch(`settings.data.${dataIndex}.metric_id`),
                              ]
                            : watch(`settings.data.${dataIndex}.metric_id`);

                        return metricIsString(metricId)
                          ? aggregateSelectOptionsFiltered.filter(
                              ({ value }) => value === 'count' || value === 'prop_num',
                            )
                          : aggregateSelectOptionsFiltered;
                      })()}
                      isActive={watch(`settings.data.${dataIndex}.aggregate`)}
                      onChange={(value) =>
                        setValue(
                          `settings.data.${dataIndex}.aggregate`,
                          value as AggregateSelectForm,
                        )
                      }
                    />
                  </div>
                </div>
                {watch('settings.group.type') !== 'metric_reverse' &&
                  (watch('settings.type') === 'piechart' || watch('settings.type') === 'donut') && (
                    <div className={fieldAndLabelBlockForAdditionalBlock}>
                      <span className={valuesLabelPathClassName}>
                        {watch('settings.type') !== 'linechart'
                          ? t('labels.label_color_col')
                          : t('labels.label_color_line')}
                      </span>
                      <div className={cn('w-[270px]', 'h-full flex items-center')}>
                        <PieChartPalette
                          onChange={(val) => setValue(`settings.data.${dataIndex}.color`, val)}
                          defaultValue={watch(`settings.data.${dataIndex}.color`)}
                        />
                      </div>
                    </div>
                  )}
                {watch('settings.group.type') !== 'metric_reverse' && (
                  <ConditionalFormattingNestedField
                    oneOnly={watch('settings.type') === 'number'}
                    nestedFieldIndex={dataIndex}
                  />
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="pb-[38px] flex flex-col gap-[20px] w-[860px]">
          <div className="text-[18px] text-1color font-bold">{t('additionally')}</div>
          <div className="flex flex-col pl-[25px]">
            <div className={fieldAndLabelBlockForAdditionalBlock + ' h-[34px]'}>
              <span className={valuesLabelPathClassName}>{t('metric_filter')}</span>
              <div className="flex items-center gap-[10px]">
                <Toggle
                  checked={!!watch('settings.filter')?.length}
                  onChecked={() => {
                    if (!watch('settings.filter')?.length) {
                      setValue('settings.filter', [
                        { id: splitMetrics?.[0].metric_id, condition: '=', value: '' },
                      ]);
                    } else {
                      setValue('settings.filter', []);
                    }
                  }}
                />
                {!!watch('settings.filter')?.length && (
                  <div className="grid grid-cols-[240px_100px_1fr] items-center gap-x-[12px] w-full">
                    <Select
                      options={metricOptions}
                      dropWidth={240}
                      defaultValue={watch('settings.filter.0.id')}
                      onChange={(val) => {
                        const value = String(val);
                        setValue('settings.filter.0.id', value);
                      }}
                    />
                    <Select
                      dropWidth={170}
                      options={getConditionsSelectOptions(watch('settings.filter.0.id'))}
                      defaultValue={watch('settings.filter.0.condition')}
                      onChange={(val) => {
                        const value = String(val);
                        setValue('settings.filter.0.condition', value);
                      }}
                    />
                    <Input
                      {...register('settings.filter.0.value')}
                      placeholder={t('filter_value_placeholder')}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    ),
    additional: (
      <div className="flex items-start pl-[25px] flex-col gap-[25px]">
        {/*TODO: header size escape on few time*/}
        {/*<div className={fieldAndLabelBlock}>*/}
        {/*  <span className={valuesLabelPathClassName}>{t('styles_header_size')}</span>*/}
        {/*  <div className={firstBlockValueWidth}>*/}
        {/*    <Input*/}
        {/*      iconColor="primary"*/}
        {/*      onClick={() => setValue('settings.styles.header_size', 18)}*/}
        {/*      icon="CancelIcon"*/}
        {/*      {...register('settings.styles.header_size')}*/}
        {/*    />*/}
        {/*  </div>*/}
        {/*</div>*/}
        {/*TODO: temporary not working  */}
        {watch('settings.type') !== 'piechart' &&
          watch('settings.type') !== 'donut' &&
          watch('settings.type') !== 'number' && (
            <div className={fieldAndLabelBlockForAdditionalBlock}>
              <span className={valuesLabelPathClassName}>{t('display_axis_vertical')}</span>
              <div className={firstBlockValueWidth}>
                <Toggle
                  checked={watch('settings.custom_properties.y_axis_show')}
                  onChecked={(status) => setValue('settings.custom_properties.y_axis_show', status)}
                />
              </div>
            </div>
          )}
        {/*TODO: temporary not working  */}
        {watch('settings.type') !== 'piechart' &&
          watch('settings.type') !== 'donut' &&
          watch('settings.type') !== 'number' && (
            <div className={fieldAndLabelBlockForAdditionalBlock}>
              <span className={valuesLabelPathClassName}>{t('display_axis_horizontal')}</span>
              <div className={firstBlockValueWidth}>
                <Toggle
                  checked={watch('settings.custom_properties.x_axis_show')}
                  onChecked={(status) => setValue('settings.custom_properties.x_axis_show', status)}
                />
              </div>
            </div>
          )}
        {/*TODO: temporary not working  */}
        {watch('settings.type') !== 'piechart' &&
          watch('settings.type') !== 'donut' &&
          watch('settings.type') !== 'number' && (
            <div className={fieldAndLabelBlockForAdditionalBlock}>
              <span className={valuesLabelPathClassName}>{t('styles_grid_vertical')}</span>
              <div className={firstBlockValueWidth}>
                <Toggle
                  checked={watch('settings.custom_properties.grid_vertical_show')}
                  onChecked={(status) =>
                    setValue('settings.custom_properties.grid_vertical_show', status)
                  }
                />
              </div>
            </div>
          )}
        {/*TODO: temporary not working  */}
        {watch('settings.type') !== 'piechart' &&
          watch('settings.type') !== 'donut' &&
          watch('settings.type') !== 'number' && (
            <div className={fieldAndLabelBlockForAdditionalBlock}>
              <span className={valuesLabelPathClassName}>{t('styles_grid_horizontal')}</span>
              <div className={firstBlockValueWidth}>
                <Toggle
                  checked={watch('settings.custom_properties.grid_horizontal_show')}
                  onChecked={(status) =>
                    setValue('settings.custom_properties.grid_horizontal_show', status)
                  }
                />
              </div>
            </div>
          )}
        {watch('settings.type') !== 'piechart' &&
          watch('settings.type') !== 'donut' &&
          watch('settings.type') !== 'number' && (
            <div className={fieldAndLabelBlockForAdditionalBlock + ' pb-[40px]'}>
              <div className={valuesLabelPathClassName}>{t('line_settings')}</div>
              <div className="flex items-center gap-[30px] ">
                <Toggle
                  checked={watch('settings.line_settings.active')}
                  onChecked={(status) => setValue('settings.line_settings.active', status)}
                />
                {watch('settings.line_settings.active') && (
                  <div className="w-[250px] flex flex-col relative ">
                    <div className="flex items-center gap-[10px]">
                      <span className={valuesLabelPathClassName}>
                        {t('conditional_formatting_from')}
                      </span>
                      <Input {...register(`settings.line_settings.value.0`)} type="number" />
                      <span className={valuesLabelPathClassName}>
                        {t('conditional_formatting_before')}
                      </span>
                      <Input {...register(`settings.line_settings.value.1`)} type="number" />
                      <ColorInput
                        color={watch('settings.line_settings.color')}
                        changeColor={(color) => setValue('settings.line_settings.color', color)}
                      />
                    </div>
                    <span className={fieldHintClassName}>{t('line_settings_hint')}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        {/* TODO: separate there  */}
        <div className="py-[20px] w-full h-[0px] ">
          <div className=" border-[0.5px] border-b_medium" />
        </div>
        {/* TODO: separate there  */}
        <h3 className="text-[18px] text-1color font-bold">{t('additionally')}</h3>
        {watch('settings.type') !== 'piechart' &&
          watch('settings.type') !== 'donut' &&
          watch('settings.type') !== 'number' && (
            <div className={fieldAndLabelBlockForAdditionalBlock + ' pb-[40px]'}>
              <span className={valuesLabelPathClassName}>{t('max_group_display')}</span>
              <div className="w-[100px] relative">
                {/*max_group_display*/}
                <Input {...register('settings.max_group_display')} type="number" />
                <span className={fieldHintClassName}>{t('max_group_display_hint')}</span>
              </div>
            </div>
          )}
        {watch('settings.type') === 'linechart' && (
          <div className={fieldAndLabelBlockForAdditionalBlock}>
            <span className={valuesLabelPathClassName}>{t('yaxis_min_value')}</span>
            <div className={firstBlockValueWidth}>
              {/*yaxis_min_value*/}
              <Toggle
                checked={watch('settings.yaxis_min_value')}
                onChecked={(status) => setValue('settings.yaxis_min_value', status)}
              />
            </div>
          </div>
        )}
        {/* only for number chart type */}

        {/* only for number chart type */}
        {watch('settings.type') !== 'number' && (
          <div className={fieldAndLabelBlockForAdditionalBlock + ' pb-[40px]'}>
            <span className={valuesLabelPathClassName}>{t('max_value_limit')}</span>
            <div className="w-[100px] relative">
              {/*max_value_limit*/}
              <Input {...register('settings.max_value_limit')} type="number" />
              <span className={fieldHintClassName}>{t('max_value_limit_hint')}</span>
            </div>
          </div>
        )}
      </div>
    ),
  };

  return (
    <EasyModal
      variant="large"
      onRemove={handlerRemove}
      onSuccess={handlerSuccess}
      label={setup?.name || t('add_chart')}
      show={isOpen}
      onClose={() => onClose(false)}
      isAutoClosable={false}
    >
      <div className="flex flex-col h-[80vh]">
        <div className="shrink-0 flex items-center gap-[10px] my-[20px] bg-basic_app_bg rounded-defaultR border-b border-b_medium">
          {settingsNavMenuData
            .filter((el) =>
              watch('settings.type') === 'number' ? el.value !== 'additional' : true,
            )
            .map(({ name, value }, index) => (
              <div
                key={index}
                onClick={() => changeActiveMenuItem(value as 'main' | 'additional' | 'axis')}
                className={settingsNavStyles(value === activeMenuItem)}
              >
                {name}
              </div>
            ))}
        </div>

        <FormProvider {...methods}>
          <form onSubmit={handleSubmit(onSubmitForm)} className="flex flex-col flex-1 min-h-0">
            <div className="flex-1 overflow-auto py-[30px] min-h-0">
              {renderMenuItems[activeMenuItem]}
            </div>

            <input className="hidden" type="submit" ref={submitFormRef} />
          </form>
        </FormProvider>
      </div>
    </EasyModal>
  );
};

export default SettingsFormBlock;
